import { useEffect, useState } from 'react';
import Icon from './Icon';
import { haptic, notify } from './telegram';

/**
 * Всплывающие уведомления.
 *
 * Половина действий в приложении заканчивалась молча: нажал «скопировать» —
 * и непонятно, скопировалось ли; сохранил права сотрудника — экран не
 * шелохнулся. Человек в такой ситуации нажимает второй раз, потом третий.
 *
 * Модальное окно тут не годится: оно требует закрыть себя, а сообщать нужно
 * о том, что уже случилось и никакого решения не требует. Поэтому полоска,
 * которая уходит сама.
 *
 * Хранилище вынесено из React намеренно: показать уведомление нужно из
 * обработчика, из `catch`, из функции вне компонента — везде, где хука нет.
 * Компонент только подписывается на изменения.
 */

export type ToastKind = 'success' | 'error' | 'info';

export interface Toast {
  id: number;
  kind: ToastKind;
  text: string;
}

/** Сколько живёт сообщение. Ошибка дольше: её читают, а не замечают. */
const LIFETIME: Record<ToastKind, number> = {
  success: 2500,
  info: 3500,
  error: 5000,
};

let items: Toast[] = [];
let nextId = 1;
const listeners = new Set<(v: Toast[]) => void>();

function publish() {
  // Новый массив на каждое изменение: React сравнивает по ссылке.
  const snapshot = [...items];
  for (const l of listeners) l(snapshot);
}

function push(kind: ToastKind, text: string) {
  const id = nextId++;
  // Больше трёх на экране — это уже не уведомление, а лента: нижние всё
  // равно не успевают прочесть, а верхние закрывают собой интерфейс.
  items = [...items.slice(-2), { id, kind, text }];
  publish();

  setTimeout(() => {
    items = items.filter((t) => t.id !== id);
    publish();
  }, LIFETIME[kind]);
}

export const toast = {
  success(text: string) {
    // Отклик телефона к слову: в Telegram это привычная часть подтверждения.
    notify('success');
    push('success', text);
  },
  error(text: string) {
    notify('error');
    push('error', text);
  },
  info(text: string) {
    haptic('light');
    push('info', text);
  },
};

/** Полоса уведомлений. Ставится один раз рядом с корнем приложения. */
export function Toasts() {
  const [list, setList] = useState<Toast[]>(items);

  useEffect(() => {
    listeners.add(setList);
    return () => {
      listeners.delete(setList);
    };
  }, []);

  if (list.length === 0) return null;

  return (
    <div className="toasts" role="status" aria-live="polite">
      {list.map((t) => (
        <div key={t.id} className={`toast toast--${t.kind}`}>
          <Icon
            name={t.kind === 'error' ? 'close' : t.kind === 'success' ? 'shield' : 'bell'}
            size={16}
          />
          <span>{t.text}</span>
        </div>
      ))}
    </div>
  );
}
